import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { CheckinSuccessProps } from "../../../types";

export default function CheckinSuccess({ checkinData, onNewCheckin }: CheckinSuccessProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          p: 3,
        }}
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 150 }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 120,
              color: "#4CAF50",
              mb: 3,
            }}
          />
        </motion.div>

        {/* Success Message */}
        <Typography
          variant="h4"
          sx={{
            color: "#7479AE",
            fontWeight: 700,
            mb: 2,
          }}
        >
          {t("admin.checkin.success.title")}
        </Typography>

        {/* Patient Name */}
        {checkinData.patientInfo && (
          <Typography
            variant="h6"
            sx={{
              color: "#666",
              mb: 4,
            }}
          >
            {checkinData.patientInfo.name}
          </Typography>
        )}

        {/* Success Details */}
        <Box
          sx={{
            bgcolor: "#E8F5E8",
            border: "1px solid #4CAF50",
            borderRadius: 2,
            p: 3,
            mb: 4,
            maxWidth: 400,
            width: "100%",
          }}
        >
          <Typography variant="body1" sx={{ color: "#2E7D32", mb: 1 }}>
            {t("admin.checkin.success.barcodeScanned")}
          </Typography>
          <Typography variant="body1" sx={{ color: "#2E7D32", mb: 1 }}>
            {t("admin.checkin.success.photoTaken")}
          </Typography>
          <Typography variant="body1" sx={{ color: "#2E7D32" }}>
            {t("admin.checkin.success.dataSaved")}
          </Typography>
        </Box>

        {/* Action Button */}
        <Button
          variant="contained"
          onClick={onNewCheckin}
          size="large"
          sx={{
            bgcolor: "#7479AE",
            color: "white",
            borderRadius: 3,
            py: 2,
            px: 4,
            fontSize: 16,
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#5a5f8a",
            },
          }}
          startIcon={<RefreshIcon />}
        >
          {t("admin.checkin.success.newCheckin")}
        </Button>
      </Box>
    </motion.div>
  );
}
